import { Handle, NodeProps, Position, useReactFlow } from '@xyflow/react'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import {
  getSymbolById,
  SYMBOL_SIZES,
} from 'features/processFlowDiagrams/data/MinimalSymbols'
import {
  getRotatedHandleConfig,
  resolveHighlightColor,
} from 'features/processFlowDiagrams/utils/helpers'
import { useRolodexEntries } from 'features/rolodex'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoProcessFlowDiagram,
  GeckoUnit,
  PfdNode,
  RolodexEntryType,
} from 'types'

import { BaseNodeWrapper } from './BaseNodeWrapper'

const SymbolContainer = styled.div<{
  width: string
  height: string
  $rotation: number
  $isSelected: boolean
  $highlightColor?: string
  $scale?: number
}>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${({ $rotation }) => $rotation}deg);

  ${({ $isSelected, $highlightColor }) =>
    $isSelected &&
    $highlightColor &&
    `
    filter: drop-shadow(0 0 6px ${$highlightColor}) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));

    svg {
      transform: scale(1.06);
      transform-origin: center;
      transition: transform 150ms ease-in-out;
    }
  `}
  transform: rotate(${({ $rotation }) => $rotation}deg)
    scale(${({ $scale }) => ($scale === null ? 1 : $scale)});
`

const SymbolSvg = styled.svg`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.forecast.text1};

  path,
  circle,
  ellipse,
  line,
  rect {
    stroke: currentColor;
    stroke-width: 4;
    fill: none;
  }
`

const NodeLabel = styled.div`
  position: absolute;
  bottom: -1.25rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.forecast.background2};
  color: ${({ theme }) => theme.forecast.text1};
  border-radius: 4px;
  padding: 0 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
`

// Handle position mapping
const HANDLE_POSITIONS = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
}

// Render a backward-compatible handle per side (legacy id and type)
// and add an additional opposite-type handle so each side can both accept and initiate connections

export const UniversalSymbolNode: React.FC<
  NodeProps<PfdNode> & { symbolId: string }
> = ({ symbolId, id, data }) => {
  const symbol = getSymbolById(symbolId)
  const { setNodes } = useReactFlow<PfdNode>()
  const { selectedNode } = useContext(ProcessFlowDiagramBuilderContext)

  const isSelected = useMemo(() => {
    if (!selectedNode) return false
    return selectedNode.id === id
  }, [selectedNode, id])

  const selectedHighlight = useMemo(() => {
    if (!selectedNode) return undefined
    const nodeData = selectedNode.data
    const status = nodeData.status
    return resolveHighlightColor(String(status))
  }, [selectedNode])

  const linkedEntryQuery = useMemo(() => {
    if (
      !data.link ||
      (data.link.entryType !== 'asset' && data.link.entryType !== 'pfd')
    ) {
      return undefined
    }

    return {
      type: [RolodexEntryType.UNIT, RolodexEntryType.PROCESS_FLOW_DIAGRAM],
      ids: [data.link.entryID],
      tags: {
        site: data.link.siteID,
      },
    }
  }, [data.link])

  const { units, processFlowDiagrams } = useRolodexEntries(linkedEntryQuery)

  const label = useMemo(() => {
    if (data.label) {
      return data.label
    }

    const linkedEntry =
      data.link?.entryType === 'asset'
        ? units?.at(0)
        : processFlowDiagrams?.at(0)

    const linkedEntryName =
      linkedEntry?.type === RolodexEntryType.UNIT
        ? (linkedEntry as GeckoUnit).data.name
        : linkedEntry?.type === RolodexEntryType.PROCESS_FLOW_DIAGRAM
          ? (linkedEntry as GeckoProcessFlowDiagram).data.metadata.name
          : undefined

    return linkedEntryName
  }, [data.label, units, processFlowDiagrams, data.link?.entryType])

  if (!symbol) {
    return (
      <BaseNodeWrapper id={id} link={data.link} setNodes={setNodes}>
        <SymbolContainer
          width='3rem'
          height='3rem'
          $rotation={0}
          $isSelected={isSelected}
          $highlightColor={selectedHighlight}>
          <div>Symbol not found: {symbolId}</div>
        </SymbolContainer>
      </BaseNodeWrapper>
    )
  }

  const size = SYMBOL_SIZES[symbol.size]

  return (
    <BaseNodeWrapper id={id} link={data.link} setNodes={setNodes}>
      <SymbolContainer
        width={`${size.size * (data.scale ?? 1)}rem`}
        height={`${size.size * (data.scale ?? 1)}rem`}
        $rotation={data.rotation ?? 0}
        $isSelected={isSelected}
        $highlightColor={selectedHighlight}>
        {symbol.handles.map((handlePos, index) => {
          const handlesAtPosition = symbol.handles.filter(
            (h) => h === handlePos
          ).length
          const baseHandleId =
            handlesAtPosition === 1 ? handlePos : `${handlePos}-${index}`

          const position =
            HANDLE_POSITIONS[handlePos as keyof typeof HANDLE_POSITIONS]

          const handleConfig = getRotatedHandleConfig(
            position,
            data.rotation ?? 0
          )

          const handleType = 'source'

          return (
            <React.Fragment key={`${handlePos}-${index}`}>
              {/* Additional opposite-type handle enables connections in both directions */}
              <Handle
                key={`${baseHandleId}-${handleType}`}
                type={handleType}
                position={handleConfig.position}
                id={`${baseHandleId}-${handleType}`}
                style={handleConfig.style}
              />
            </React.Fragment>
          )
        })}

        <SymbolSvg viewBox={symbol.viewBox}>{symbol.svgContent}</SymbolSvg>
      </SymbolContainer>
      {label && <NodeLabel title={label}>{label}</NodeLabel>}
    </BaseNodeWrapper>
  )
}

// Universal preview component
export const UniversalSymbolPreview: React.FC<{
  symbolId: string
  scale?: number
}> = ({ symbolId, scale = 0.7 }) => {
  const symbol = getSymbolById(symbolId)

  if (!symbol) return <div>Symbol not found</div>

  const size = SYMBOL_SIZES[symbol.size]

  return (
    <SymbolContainer
      width={`${size.size}rem`}
      height={`${size.size}rem`}
      $rotation={0}
      $isSelected={false}
      style={{ transform: `scale(${scale})` }}
      $scale={scale}>
      <SymbolSvg viewBox={symbol.viewBox}>{symbol.svgContent}</SymbolSvg>
    </SymbolContainer>
  )
}
